import { useTranslation } from "react-i18next";
import { useIntl, IntlShape } from "react-intl";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { provincesSimplified } from "@/constants/provinces";
import { z } from "zod";
import { TFunction } from "i18next";
import { FieldErrors, useForm, useFieldArray, type DefaultValues } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import {
  ComboboxInputField,
  DateInputField,
  SwitchInputField,
} from "@/components/shared/form";
import { Plus, Loader2, Trash } from "lucide-react";
import { useEffect, useState } from "react";
import { useAddHolidaysMutationHook } from "@/hooks";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { isValid } from "date-fns";
import { generateHolidayDates, getHolidayDateKey } from "./dateGeneration";
import { formatDateLocale } from "@/utils/helpers";
import { createDateSchema } from "@/utils/form";

/**
 * Checks for duplicate dates in the generated dates array.
 * Returns an error object if duplicates are found, null otherwise.
 */
function checkForDuplicateDates(
  dates: Date[],
  intl: IntlShape,
  t: TFunction
): { date: string; error: string } | null {
  if (dates.length <= 1) return null;

  const dateStrings = dates.map((d) => d.toISOString().split("T")[0]);
  const uniqueDates = new Set(dateStrings);

  if (dateStrings.length === uniqueDates.size) return null;

  const seenDates = new Set<string>();
  const duplicateDates = new Set<string>();

  for (const dateString of dateStrings) {
    if (seenDates.has(dateString)) {
      duplicateDates.add(dateString);
    } else {
      seenDates.add(dateString);
    }
  }

  const duplicateDatesForDisplay = Array.from(duplicateDates).map((isoDate) => {
    const date = new Date(isoDate + "T00:00:00.000Z");
    return formatDateLocale(intl, date);
  });

  return {
    date: duplicateDatesForDisplay.join(", "),
    error: t("addholiday.duplicatesaftergeneration"),
  };
}

interface AddHolidayModalProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
}

const createHolidaySchema = (t: TFunction) =>
  z.object({
    province: z.string({ message: t("schemas:form.required") }),
    dates: z
      .array(
        z.object({
          date: createDateSchema(t),
          isRecurring: z.boolean(),
        })
      )
      .min(1, t("addholiday.atleastonedate"))
      .superRefine((dates, ctx) => {
        const validDates = dates
          .map((d, index) => ({ date: d.date, index }))
          .filter(
            ({ date }) => date instanceof Date && isValid(date)
          );

        const dateCounts = new Map<string, number[]>();

        validDates.forEach(({ date, index }) => {
          const key = getHolidayDateKey(date);
          if (!key) return;

          if (!dateCounts.has(key)) {
            dateCounts.set(key, []);
          }
          dateCounts.get(key)!.push(index);
        });

        dateCounts.forEach((indices) => {
          if (indices.length > 1) {
            indices.forEach((index) => {
              ctx.addIssue({
                code: "custom",
                message: t("addholiday.duplicatedate"),
                path: [index, "date"],
              });
            });
          }
        });
      }),
  });

type HolidayFormData = z.infer<ReturnType<typeof createHolidaySchema>>;
type HolidayDateRowInput = NonNullable<
  DefaultValues<HolidayFormData>["dates"]
>[number];

const holidayDefaultValues: DefaultValues<HolidayFormData> = {
  province: undefined,
  dates: [{ date: undefined, isRecurring: false }],
};

const emptyDateRow = (): HolidayDateRowInput => ({
  date: undefined,
  isRecurring: false,
});

/**
 * Modal component for adding new holidays.
 * Allows selection of province, whether the holiday is recurring, and multiple dates.
 */
function AddHolidayModal({ open, onOpenChange }: AddHolidayModalProps) {
  const { t } = useTranslation(["modals", "common"]);
  const intl = useIntl();
  const { addHolidaysMutation, loading: addHolidaysLoading } =
    useAddHolidaysMutationHook();

  const [invalidDates, setInvalidDates] = useState<
    Array<{ date: string; error: string }>
  >([]);

  const holidaySchema = createHolidaySchema(t);
  const form = useForm<HolidayFormData>({
    resolver: zodResolver(holidaySchema),
    mode: "onChange",
    defaultValues: holidayDefaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "dates",
  });

  const handleSubmit = (data: HolidayFormData) => {
    const result = generateHolidayDates(data.dates, intl, t);

    setInvalidDates(result.errors);

    if (result.success && result.dates.length > 0) {
      const duplicateError = checkForDuplicateDates(result.dates, intl, t);
      if (duplicateError) {
        setInvalidDates([duplicateError]);
        return;
      }
    }

    const transformedData = {
      province: data.province,
      dates: result.dates,
    };

    if (result.success) {
      addHolidaysMutation({ variables: { input: transformedData } }).finally(
        () => {
          onOpenChange(false);
        }
      );
    }
  };

  const onInvalid = (errors: FieldErrors<HolidayFormData>) => {
    console.error(errors);
  };

  const addDateRow = () => {
    append(emptyDateRow() as HolidayFormData["dates"][number]);
  };

  const removeDateRow = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  useEffect(() => {
    if (open) {
      form.reset(holidayDefaultValues);
      setInvalidDates([]);
    }
  }, [open, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="px-2">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit, onInvalid)}
            className="space-y-6"
            id="add-holiday-form"
          >
            <DialogHeader className="px-4">
              <DialogTitle>{t("addholiday.title")}</DialogTitle>
              <DialogDescription>{t("addholiday.description")}</DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-4 max-h-[75vh] py-2 pl-4 pr-4">
              <div className="grid grid-cols-1 gap-4 items-start">
                <ComboboxInputField
                  form={form}
                  name="province"
                  label={t("addholiday.inputs.province.label")}
                  options={provincesSimplified.map((province) => ({
                    label: province.name,
                    value: province.code,
                  }))}
                  className="w-full"
                />

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {t("addholiday.inputs.dates.label")}
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addDateRow}
                      className="h-8 w-8 p-0"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 grid grid-cols-2 gap-4 text-sm text-slate-500">
                        <div>{t("addholiday.inputs.date.label")}</div>
                        <div className="flex items-center gap-2">
                          {t("addholiday.inputs.isRecurring.label")}
                          <HoverCard>
                            <HoverCardTrigger asChild>
                              <InfoCircledIcon className="h-4 w-4 text-slate-500" />
                            </HoverCardTrigger>
                            <HoverCardContent className="text-sm">
                              <p className="max-w-[320px]">
                                {t("addholiday.inputs.isRecurring.note")}
                              </p>
                            </HoverCardContent>
                          </HoverCard>
                        </div>
                      </div>
                      {fields.length > 1 && <div className="w-10" />}
                    </div>

                    {fields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <div className="flex-1 grid grid-cols-2 gap-4">
                          <DateInputField
                            form={form}
                            name={`dates.${index}.date`}
                            className="w-full"
                            allowOlderDates
                          />
                          <SwitchInputField
                            form={form}
                            name={`dates.${index}.isRecurring`}
                            className="items-start pt-2.5"
                          />
                        </div>
                        {fields.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeDateRow(index)}
                          >
                            <Trash className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  {form.formState.errors.dates && (
                    <p className="text-sm text-destructive">
                      {form.formState.errors.dates.message}
                    </p>
                  )}

                  {invalidDates.length > 0 && (
                    <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                      <h4 className="text-sm font-medium text-destructive mb-2">
                        {t("addholiday.invaliddates")}
                      </h4>
                      <ul className="text-sm text-destructive space-y-1">
                        {invalidDates.map((invalid, index) => (
                          <li key={index}>
                            {invalid.date}: {invalid.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" type="button">
                  {t("common:buttons.cancel")}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={addHolidaysLoading}>
                {addHolidaysLoading && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {t("common:buttons.add")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default AddHolidayModal;
